import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { PhoneIphone as PhoneIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { getAuthHeaders } from '../../utils/auth';

const emptyForm = {
  min_version_ios: '',
  min_version_android: '',
  store_url_ios: '',
  store_url_android: '',
};

const VERSION_PATTERN = /^\d+(\.\d+){0,2}$/;

// updated_at is stored in UTC without an offset.
const parseUtc = (value) => parseISO(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);

/**
 * Forced update of the mobile app. An installed app older than the minimum
 * version of its platform shows only an "update required" screen with a link
 * to the store. The version is the GitHub release tag the app was built from
 * (v1.1.0 -> 1.1.0); an empty field means no minimum.
 */
const MobileAppManagement = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const applySettings = (settings) => {
    setFormData({
      min_version_ios: settings.min_version_ios || '',
      min_version_android: settings.min_version_android || '',
      store_url_ios: settings.store_url_ios || '',
      store_url_android: settings.store_url_android || '',
    });
    setUpdatedAt(settings.updated_at || null);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/mobile-app/settings`, { headers });
        if (response.ok) {
          applySettings(await response.json());
          setError('');
        } else {
          setError(
            response.status === 403 || response.status === 401
              ? 'Brak uprawnień do ustawień aplikacji'
              : 'Nie udało się pobrać ustawień aplikacji'
          );
        }
      } catch (err) {
        setError('Błąd połączenia z serwerem');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const versionError = (value) => value.trim() !== '' && !VERSION_PATTERN.test(value.trim());

  const handleSubmit = async () => {
    if (versionError(formData.min_version_ios) || versionError(formData.min_version_android)) {
      setError('Wersja musi mieć postać 1.2.3');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/mobile-app/settings`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        applySettings(await response.json());
        setSuccess('Zapisano ustawienia aplikacji');
      } else {
        const data = await response.json().catch(() => ({}));
        const detail = Array.isArray(data.detail) ? data.detail[0]?.msg : data.detail;
        setError(detail || 'Nie udało się zapisać ustawień');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PhoneIcon />
        <Typography variant="h5">Aplikacja mobilna</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Wymuszenie aktualizacji</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Aplikacja w wersji niższej niż minimalna pokaże tylko ekran „Zaktualizuj aplikację” z przyciskiem do sklepu.
          Puste pole wyłącza wymuszenie. Ustaw minimum dopiero, gdy nowa wersja jest już dostępna w sklepie.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Minimalna wersja iOS"
              placeholder="np. 1.1.0"
              value={formData.min_version_ios}
              onChange={(e) => handleChange('min_version_ios', e.target.value)}
              error={versionError(formData.min_version_ios)}
              helperText={versionError(formData.min_version_ios) ? 'Format: 1.2.3' : ' '}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Minimalna wersja Android"
              placeholder="np. 1.1.0"
              value={formData.min_version_android}
              onChange={(e) => handleChange('min_version_android', e.target.value)}
              error={versionError(formData.min_version_android)}
              helperText={versionError(formData.min_version_android) ? 'Format: 1.2.3' : ' '}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Link do App Store"
              placeholder="https://apps.apple.com/pl/app/..."
              value={formData.store_url_ios}
              onChange={(e) => handleChange('store_url_ios', e.target.value)}
              helperText="Bez linku ekran na iOS nie pokaże przycisku do sklepu"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Link do Google Play"
              placeholder="https://play.google.com/store/apps/details?id=pl.natchnieniestudio.app"
              value={formData.store_url_android}
              onChange={(e) => handleChange('store_url_android', e.target.value)}
              helperText="Puste = domyślny link do aplikacji w Google Play"
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {updatedAt ? `Ostatnia zmiana: ${format(parseUtc(updatedAt), 'dd.MM.yyyy HH:mm')}` : 'Jeszcze nie zapisano'}
          </Typography>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Zapisz'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default MobileAppManagement;
